/**
 * Engine pipeline — one place that runs the condition engine + calculated
 * fields + validation engine in the correct order.
 *
 * Why this lives here, not in the renderer:
 *   - The validation engine's output depends on RenderState (which fields are
 *     visible, which are required, which option keys survive filtering).
 *   - Calling sites that compute one and forget the other drift over time.
 *   - Putting the pipeline in a single function makes the dependency
 *     condition → validation explicit and impossible to skip.
 *
 * Pure: same `(definition, values)` always returns the same EngineSnapshot.
 */
import type {
  FieldDefinition,
  ConditionalRule,
} from './definition-types'
import type { RenderState, BlockingError, SoftWarning } from './types'
import {
  evaluateConditionalLogic,
  computeCalculatedFields,
} from './condition-engine'
import {
  detectBlockingErrors,
  detectSoftWarnings,
} from './validation-engine'

export interface EngineSnapshot {
  /** Input values merged with the evaluated calculated-field results. */
  computedValues: Record<string, unknown>
  /** Per-field visibility / required / enabled / filteredOptions. */
  renderStates: Record<string, RenderState>
  /** Field-key → blocking error. Visible-only fields. */
  blockingErrors: Record<string, BlockingError>
  /** All visible-field soft warnings, flat list. */
  softWarnings: SoftWarning[]
}

export function runFormEngine(
  fields: FieldDefinition[],
  conditionalRules: ConditionalRule[],
  rawValues: Record<string, unknown>,
): EngineSnapshot {
  const calcResults = computeCalculatedFields(fields, rawValues)
  const computedValues = { ...rawValues, ...calcResults }
  // evaluateConditionalLogic mutates `values` for set_default_value actions —
  // pass a fresh copy so callers' state is not retroactively mutated.
  const renderStates = evaluateConditionalLogic(
    fields,
    conditionalRules,
    { ...computedValues },
  )
  const blockingErrors = detectBlockingErrors(fields, renderStates, computedValues)
  const softWarnings  = detectSoftWarnings (fields, renderStates, computedValues)
  return { computedValues, renderStates, blockingErrors, softWarnings }
}

/**
 * Touched-state gating (Step 4): errors live for every visible field as soon
 * as the engine runs, but the UI should only SHOW required-empty / blocking
 * errors for fields the user has interacted with. Submit-time validation
 * marks every field touched to surface the remaining problems at once.
 *
 * `gateErrorsByTouched` returns a filtered view of the engine's blocking
 * errors map suitable for inline rendering.
 */
export function gateErrorsByTouched(
  errors: Record<string, BlockingError>,
  touched: Record<string, boolean>,
  submitAttempted: boolean,
): Record<string, BlockingError> {
  if (submitAttempted) return errors
  const out: Record<string, BlockingError> = {}
  for (const k of Object.keys(errors)) {
    if (touched[k]) out[k] = errors[k]
  }
  return out
}

/**
 * Helper map: field_key → SoftWarning[] for inline rendering.
 */
export function groupWarningsByField(warnings: SoftWarning[]): Record<string, SoftWarning[]> {
  const map: Record<string, SoftWarning[]> = {}
  for (const w of warnings) {
    if (!map[w.fieldKey]) map[w.fieldKey] = []
    map[w.fieldKey].push(w)
  }
  return map
}
